import type { ChangeEvent, ReactNode } from 'react';
import { CheckCircle, UploadSimple } from '@phosphor-icons/react';
import { cn } from '@/lib/utils/cn';
import { formatFileSize } from '@/lib/utils/formatFileSize';

export function Label({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {icon}
      {children}
    </label>
  );
}

export function SourceToggle({
  mode,
  onChange,
}: {
  mode: 'file' | 'text';
  onChange: (mode: 'file' | 'text') => void;
}) {
  return (
    <div className="inline-flex shrink-0 self-start rounded-lg border border-white/10 bg-white/[0.04] p-0.5 text-[11px] sm:self-auto">
      {(['file', 'text'] as const).map((m) => (
        <button
          key={m}
          type="button"
          aria-pressed={mode === m}
          onClick={() => onChange(m)}
          className={cn(
            'rounded-md px-2 py-1 transition',
            mode === m
              ? 'bg-neurax-electric/20 text-neurax-electric'
              : 'text-muted-foreground hover:text-white'
          )}
        >
          {m === 'file' ? 'Upload file' : 'Paste text'}
        </button>
      ))}
    </div>
  );
}

export function FileDropZone({
  label,
  required,
  accept,
  file,
  onFile,
  hint,
}: {
  label: string;
  required?: boolean;
  accept: string;
  file: File | null;
  onFile: (f: File | null) => void;
  hint?: string;
}) {
  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    onFile(f);
    // Allow re-picking the same file after it was rejected or removed
    e.target.value = '';
  }
  return (
    <div className="space-y-1.5">
      {label && (
        <Label icon={<UploadSimple className="h-3.5 w-3.5" />}>
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </Label>
      )}
      <label
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-xl border border-dashed px-4 py-3 transition',
          file
            ? 'border-neurax-mint/40 bg-neurax-mint/[0.06]'
            : 'border-white/10 bg-white/[0.02] hover:border-neurax-electric/40 hover:bg-neurax-electric/[0.04]'
        )}
      >
        <div
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg',
            file ? 'bg-neurax-mint/15 text-neurax-mint' : 'bg-white/[0.05] text-muted-foreground'
          )}
        >
          {file ? <CheckCircle className="h-4 w-4" /> : <UploadSimple className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          {file ? (
            <>
              <p className="truncate text-sm font-medium text-white">{file.name}</p>
              <p className="text-[11px] text-muted-foreground">
                {formatFileSize(file.size)} · {file.type || 'unknown'}
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-white">Click to upload</p>
              {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
            </>
          )}
        </div>
        <input type="file" accept={accept} onChange={onChange} className="hidden" />
      </label>
    </div>
  );
}
